import { CheckSquare, Square, StarFull, StarOff } from "@tamagui/lucide-icons";
import type { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import React from "react";
import { ScrollView, XStack, YStack } from "tamagui";
import { useApi } from "../core/apis";
import { getDayJs } from "../core/dayJs";
import { devGetPlaniras } from "../core/firebaseInit";
import { useLoadUsersInPlan } from "../core/hooks/useLoadUsersInPlan";
import { useStores } from "../core/stores";
import { type PlanModelProgress, PlanModelStep } from "../plans/PlanModel";
import { PlanQuickstartUI } from "../plans/PlanQuickstartUI";
import { PlanUI } from "../plans/PlanUI";
import { Body } from "../ui/Body";
import { Button } from "../ui/Button";
import { Label } from "../ui/Label";
import { LoadingCircular } from "../ui/LoadingCircular";
import { Screen } from "../ui/Screen";
import { Separator } from "../ui/Separator";

function useHasLoadedAlready(data: boolean) {
  const [hasLoaded, setHasLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!hasLoaded && data) {
      setHasLoaded(true);
    }
  }, [hasLoaded, data]);

  return hasLoaded;
}

const ScoreArea = observer(
  ({
    progress,
    ts,
    tsUpdated,
  }: {
    progress: PlanModelProgress;
    ts?: Timestamp;
    tsUpdated?: Timestamp;
  }) => {
    return (
      <YStack gap="$small">
        <Label>Score: {progress.rating}</Label>
        {ts && <Body muted>Created {getDayJs(ts)}</Body>}
        {tsUpdated && <Body muted>Updated {getDayJs(tsUpdated)}</Body>}
        <Separator />
        <XStack gap="$medium">
          {progress[PlanModelStep.hasMultipleUsers] ? (
            <CheckSquare />
          ) : (
            <Square />
          )}
          <Body>Has users &gt; 1</Body>
        </XStack>

        <XStack gap="$medium">
          {progress[PlanModelStep.hasMoreThanFiveUsers] ? (
            <CheckSquare />
          ) : (
            <Square />
          )}
          <Body>Has users &gt; 5</Body>
        </XStack>

        <XStack gap="$medium">
          {progress[PlanModelStep.hasUsersWithMultiplePlans] ? (
            <CheckSquare />
          ) : (
            <Square />
          )}
          <Body>Has users with multiple plans</Body>
        </XStack>

        <XStack gap="$medium">
          {progress[PlanModelStep.hasDescription] ? (
            <CheckSquare />
          ) : (
            <Square />
          )}
          <Body>Has description</Body>
        </XStack>

        <XStack gap="$medium">
          {progress[PlanModelStep.hasSetCardToDone] ? (
            <CheckSquare />
          ) : (
            <Square />
          )}
          <Body>Has set a card to done</Body>
        </XStack>

        <XStack gap="$medium">
          {progress[PlanModelStep.hasClickedOnShare] ? (
            <CheckSquare />
          ) : (
            <Square />
          )}
          <Body>Has clicked on share</Body>
        </XStack>

        <XStack gap="$medium">
          {progress[PlanModelStep.hasComments] ? <CheckSquare /> : <Square />}
          <Body>Has comments</Body>
        </XStack>

        <XStack gap="$medium">
          {progress[PlanModelStep.hasAddedCard] ? <CheckSquare /> : <Square />}
          <Body>Has added a card</Body>
        </XStack>
      </YStack>
    );
  },
);

const DevPlanViewer = observer(({ planUid }: { planUid: string }) => {
  const { plansStore } = useStores();
  const { plansApi } = useApi();

  const plan = plansStore.byId.get(planUid);

  useLoadUsersInPlan(planUid);

  const hasLoadedAlready = useHasLoadedAlready(plan?.isUsersLoaded ?? false);

  React.useEffect(() => {
    const unsub = plansApi.watchItem(planUid);
    return () => {
      unsub();
    };
  }, [plansApi, planUid]);

  if (!plan || !hasLoadedAlready) {
    return <LoadingCircular />;
  }

  const isQuickstart = plan.isQuickstart;
  const tsUpdated = plan.lastUpdatedAt;

  return (
    <YStack gap="$large" maxWidth={500}>
      <ScoreArea
        progress={plan.data.progress}
        ts={plan.data.ts}
        tsUpdated={tsUpdated}
      />
      <Separator />
      {isQuickstart && <PlanQuickstartUI uid={planUid} />}
      {!isQuickstart && <PlanUI uid={planUid} />}
      <Separator />
      <XStack gap="$medium">
        <Button
          icon={plan.data.dev.starred ? StarFull : StarOff}
          onPress={() => {
            plan.data.dev.starred = !plan.data.dev.starred;
            void plansApi.update(plan.uid, { dev: plan.data.dev });
          }}
        >
          Toggle favorite
        </Button>

        <Button
          onPress={() => {
            plan.data.dev.skipped = true;
            void plansApi.update(plan.uid, { dev: plan.data.dev });
          }}
        >
          Skip in viewer
        </Button>
      </XStack>
    </YStack>
  );
});

type QueryType = "recent" | "withRating" | "starred";

export const DevPlansScreen = observer(() => {
  const [queryType, setQueryType] = React.useState<QueryType | undefined>(
    undefined,
  );

  const [currentAmount, setCurrentAmount] = React.useState(5);

  const [planUids, setPlanUids] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (queryType) {
      devGetPlaniras(currentAmount, queryType).then((p: string[]) => {
        setPlanUids(p);
      });
    }
  }, [queryType, currentAmount]);

  return (
    <Screen maxWidth={"auto"}>
      <XStack gap="$medium" marginHorizontal="$large">
        <Button
          onPress={() => {
            setQueryType("withRating");
          }}
          type={queryType === "withRating" ? "primary" : "secondary"}
        >
          By rating
        </Button>

        <Button
          onPress={() => {
            setQueryType("recent");
          }}
          type={queryType === "recent" ? "primary" : "secondary"}
        >
          By recency
        </Button>

        <Button
          onPress={() => {
            setQueryType("starred");
          }}
          type={queryType === "starred" ? "primary" : "secondary"}
        >
          Only starred
        </Button>

        {[5, 10, 15, 20].map((nr) => (
          <Button
            key={nr}
            onPress={() => {
              setCurrentAmount(nr);
            }}
            type={currentAmount === nr ? "ghost" : "secondary"}
          >
            {nr}
          </Button>
        ))}
      </XStack>

      <ScrollView horizontal>
        <XStack gap="$large" marginHorizontal="$large" marginBottom="$large">
          {planUids.map((planUid) => (
            <DevPlanViewer key={planUid} planUid={planUid} />
          ))}
        </XStack>
      </ScrollView>
    </Screen>
  );
});
